
import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import { API_BASE_URL } from '../../constants/api';

export default function PartnerSupportPage() {
  const [selectedTicket, setSelectedTicket] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    partnerId: '',
    category: '',
    priority: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const supportCategories = [
    { id: 'account', name: 'Account Issues', icon: 'ri-user-line' },
    { id: 'payment', name: 'Payment & Earnings', icon: 'ri-money-rupee-circle-line' },
    { id: 'jobs', name: 'Job Related', icon: 'ri-briefcase-line' },
    { id: 'app', name: 'App Technical Issues', icon: 'ri-smartphone-line' },
    { id: 'training', name: 'Training & Skills', icon: 'ri-book-line' },
    { id: 'other', name: 'Other', icon: 'ri-question-line' }
  ];

  const commonIssues = [
    {
      id: 'payment-delayed',
      title: 'My payment is delayed. When will I receive it?',
      answer: 'Payments are processed every Monday for the previous week. If your payment is delayed beyond Tuesday, please contact support with your Partner ID. Ensure your bank details are correct in your profile.'
    },
    {
      id: 'job-cancellation',
      title: 'Customer cancelled the job. Will I get compensation?',
      // Fixed string quoting – now uses double quotes so apostrophes don’t need escaping
      answer: "If a customer cancels within 2 hours of the scheduled time and you've already started traveling, you'll receive 50% of the service fee as compensation. This will be added to your next weekly payout."
    },
    {
      id: 'app-not-working',
      title: 'The partner app is not working properly',
      answer: 'Try clearing the app cache, updating to the latest version, or restarting your phone. If the issue persists, please report it with your device details and we\'ll provide technical support.'
    },
    {
      id: 'rating-dispute',
      title: 'I received an unfair rating from a customer',
      answer: 'If you believe a rating is unfair, you can dispute it within 48 hours. Provide details about the service and any evidence. Our team will review and may remove or adjust the rating if warranted.'
    },
    {
      id: 'job-not-received',
      title: 'I\'m not receiving job requests',
      answer: 'Ensure your availability is set to "Online" in the app, your location services are enabled, and your profile is complete. Job allocation depends on location, ratings, and service categories you\'ve selected.'
    },
    {
      id: 'equipment-damaged',
      title: 'My equipment was damaged during service. What should I do?',
      answer: 'Report equipment damage immediately through the app or contact support. If it occurred due to normal service usage, you may be eligible for compensation under our partner protection program.'
    }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const response = await fetch(`${API_BASE_URL}/webforms/partner-support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            partnerId: '',
            category: '',
            priority: '',
            message: ''
          });
          setSubmitStatus('idle');
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white">
      <Navbar />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-purple-600 to-purple-700 text-white relative overflow-hidden"
               style={{
                 backgroundImage: `url('https://readdy.ai/api/search-image?query=service%20partner%20getting%20help%20from%20support%20team%2C%20professional%20assistance%20with%20tools%20and%20smartphone%2C%20purple%20and%20white%20theme%2C%20partner%20success%20support%20environment%2C%20helpful%20customer%20service%20for%20workers&width=1200&height=500&seq=partner-support-hero&orientation=landscape')`,
                 backgroundSize: 'cover',
                 backgroundPosition: 'center'
               }}>
        <div className="absolute inset-0 bg-purple-600/80"></div>
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 className="text-4xl font-bold mb-4">Partner Support</h1>
          <p className="text-xl max-w-2xl mx-auto">
            We're here to support your success. Get help with earnings, jobs, app issues, and more
          </p>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-8 bg-white shadow-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              <a href="tel:93074 19845" className="flex items-center p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors cursor-pointer">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-phone-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Partner Helpline</h3>
                  <p className="text-green-600 font-medium">93074 19845</p>
                </div>
              </a>

              <a href="mailto:partners@baainearme.com" className="flex items-center p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer">
                <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-mail-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Email Support</h3>
                  <p className="text-blue-600 font-medium">partners@baainearme.com</p>
                </div>
              </a>

              <div className="flex items-center p-4 bg-purple-50 rounded-xl cursor-pointer">
                <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mr-4">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800">Support Hours</h3>
                  <p className="text-purple-600 font-medium">6 AM - 11 PM Daily</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Partner Issues */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Common Partner Questions</h2>
            <p className="text-xl text-gray-600">Quick solutions to frequently reported issues</p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {commonIssues.map((issue) => (
                <div key={issue.id} className="bg-white rounded-xl shadow-sm border border-gray-100">
                  <button
                    onClick={() => setSelectedTicket(selectedTicket === issue.id ? null : issue.id)}
                    className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <h3 className="font-semibold text-gray-800 pr-4">{issue.title}</h3>
                    <i className={`ri-arrow-down-s-line text-gray-400 transition-transform flex-shrink-0 ${
                      selectedTicket === issue.id ? 'rotate-180' : ''
                    }`}></i>
                  </button>
                  {selectedTicket === issue.id && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600 leading-relaxed">{issue.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Partner Support Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-800 mb-4">Need More Help?</h2>
              <p className="text-xl text-gray-600">Submit a support ticket and our partner success team will assist you</p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
              <form id="partner-support-form" onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="partnerId" className="block text-sm font-semibold text-gray-700 mb-2">
                      Partner ID *
                    </label>
                    <input
                      type="text"
                      id="partnerId"
                      name="partnerId"
                      required
                      value={formData.partnerId}
                      onChange={(e) => setFormData(prev => ({ ...prev, partnerId: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your Partner ID"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="category" className="block text-sm font-semibold text-gray-700 mb-2">
                      Issue Category *
                    </label>
                    <div className="relative">
                      <select
                        id="category"
                        name="category"
                        required
                        value={formData.category}
                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select issue category</option>
                        {supportCategories.map((category) => (
                          <option key={category.id} value={category.id}>{category.name}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="priority" className="block text-sm font-semibold text-gray-700 mb-2">
                      Priority Level *
                    </label>
                    <div className="relative">
                      <select
                        id="priority"
                        name="priority"
                        required
                        value={formData.priority}
                        onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm appearance-none bg-white"
                      >
                        <option value="">Select priority</option>
                        <option value="low">Low - General inquiry</option>
                        <option value="medium">Medium - Issue affecting work</option>
                        <option value="high">High - Urgent, affecting earnings</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Describe Your Issue *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    required
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm resize-none"
                    placeholder="Please provide detailed information about your issue..."
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </div>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-check-line text-green-600 mr-2"></i>
                      <p className="text-green-800 font-semibold">Support ticket submitted successfully!</p>
                    </div>
                    <p className="text-green-700 text-sm mt-1">
                      Our partner success team will contact you within 12 hours.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center">
                      <i className="ri-error-warning-line text-red-600 mr-2"></i>
                      <p className="text-red-800 font-semibold">Please fill in all required fields</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-purple-600 text-white py-4 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Submitting Ticket...
                    </span>
                  ) : (
                    'Submit Support Ticket'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-16 bg-purple-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">Partner Resources</h2>
            <p className="text-xl text-gray-600">Tools and guides to help you succeed</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <Link to="/earnings-guide" className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-money-rupee-circle-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Earnings Guide</h3>
              <p className="text-gray-600">Learn how to maximize your earnings and understand payment cycles</p>
            </Link>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-book-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Training Materials</h3>
              <p className="text-gray-600">Access training videos, service guides, and skill development resources</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-2xl text-purple-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">App Guide</h3>
              <p className="text-gray-600">Step-by-step guide to using the partner app effectively</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              {/* BNM logo (SVG) — scalable, color via currentColor */}
<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 420 160"
  className="h-10 text-white"   // change to text-purple-600 etc.
  role="img"
  aria-label="BNM · Baainearme"
>
  <g fill="none" stroke="currentColor" strokeWidth="12" strokeLinecap="round">
    {/* Left ring */}
    <circle cx="50" cy="60" r="40" />
    {/* Vertical divider */}
    <line x1="110" y1="20" x2="110" y2="120" />
  </g>

  {/* Text block */}
  <g fill="currentColor">
    {/* BNM */}
    <text
      x="140"
      y="72"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="800"
      fontSize="56"
    >
      BNM
    </text>

    {/* Baainearme */}
    <text
      x="140"
      y="120"
      fontFamily="Poppins, Inter, Arial, sans-serif"
      fontWeight="700"
      fontSize="32"
    >
      Baainearme
    </text>
  </g>
</svg>

              <p className="text-gray-400">
                Supporting our partners every step of the way
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">For Partners</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/apply" className="hover:text-white cursor-pointer">Apply Now</Link></li>
                <li><Link to="/partner-support" className="hover:text-white cursor-pointer">Partner Support</Link></li>
                <li><Link to="/earnings-guide" className="hover:text-white cursor-pointer">Earnings Guide</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/customer" className="hover:text-white cursor-pointer">For Customers</Link></li>
                <li><Link to="/about" className="hover:text-white cursor-pointer">About Us</Link></li>
                <li><Link to="/faqs" className="hover:text-white cursor-pointer">FAQs</Link></li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/partner-terms" className="hover:text-white cursor-pointer">Terms & Conditions</Link></li>
                <li><Link to="/partner-privacy" className="hover:text-white cursor-pointer">Privacy Policy</Link></li>
                <li><Link to="/refund-policy" className="hover:text-white cursor-pointer">Refund Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Baainearme. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
